/**
 * Supervisor control actions for the DEMO Campaign Summary dashboard.
 *
 * This module is the demo seam only. The real transports — which REST endpoint
 * each call control hits, which socket event each agent control emits, and why —
 * live in `./live-actions.ts`, and this delegates to them whenever `DEMO_MODE`
 * is off. There is deliberately ONE implementation of the transports: when this
 * file carried its own copy, a change to the real path had to be made twice and
 * the live dashboard was one forgotten edit away from diverging.
 */
import { useCallback } from 'react'
import { DEMO_MODE } from './demo-mode.js'
import { useUiStore } from '../../../store/uiStore.js'
import {
  useLiveSupervisorActions,
  ACTION_LABEL,
  type CallAction,
  type SupervisorActions,
} from './live-actions.js'

export type { CallAction, SupervisorActions }

export function useSupervisorActions(
  /** DEMO MODE ONLY (phase 1) — applies agent actions to the simulator. */
  demoAgentAction?: (action: 'force_pause' | 'force_logout', userId: string) => void,
): SupervisorActions {
  const addNotification = useUiStore((s) => s.addNotification)
  const live = useLiveSupervisorActions()

  const invokeCallAction = useCallback(
    (action: CallAction, callId: string) => {
      // DEMO MODE (phase 1): the simulated calls have no ARI channel behind
      // them, so the REST call would 404 on an unknown callId and every
      // control would look broken during the demo. Acknowledge locally
      // instead. Removed with the flag — the real path is untouched.
      if (DEMO_MODE) {
        addNotification(`${ACTION_LABEL[action]} — applied to call`, 'info')
        return
      }
      live.invokeCallAction(action, callId)
    },
    [addNotification, live],
  )

  // DEMO MODE (phase 1): simulated agents have no presence key, so the socket
  // event would be applied to nothing. `demoAgentAction` lets the caller move
  // the simulated agent instead, so pause/logout visibly work in the demo.
  const forcePause = useCallback(
    (agentId: string, agentName: string) => {
      if (DEMO_MODE) {
        demoAgentAction?.('force_pause', agentId)
        addNotification(`${agentName} paused`, 'info')
        return
      }
      live.forcePause(agentId, agentName)
    },
    [addNotification, demoAgentAction, live],
  )

  const forceLogout = useCallback(
    (agentId: string, agentName: string) => {
      if (DEMO_MODE) {
        demoAgentAction?.('force_logout', agentId)
        addNotification(`${agentName} logged out`, 'info')
        return
      }
      live.forceLogout(agentId, agentName)
    },
    [addNotification, demoAgentAction, live],
  )

  return { invokeCallAction, forcePause, forceLogout }
}
